import type { ReactNode } from "react";
import { memo, useCallback, useMemo, useRef, useState } from "react";

import clsx from "clsx";

import { useTranslation } from "react-i18next";

import { useSearchParams } from "react-router-dom";

import FilterIcon from "assets/icons/outlined/filter.svg?react";

import { useEffectOnce } from "shared/hooks";
import type { BaseBlockFilterModel } from "shared/types/FilterModel";
import { Button, PopoverBox, SkeletonBase } from "shared/uikit";
import Text from "shared/uikit/Text";
import { ButtonSizesEnum, ButtonVariantsEnum, MenuListPositionOpts, TextVariantsEnum } from "shared/uikit/types";

import type { FilterBlockModel } from "./FilterBlock";
import { FilterBlockTypes } from "./FilterBlock";
import FilterBlocks from "./FilterBlocks";
import { FilterCardWrapper, FilterContentWrapper } from "./styles";

export interface FilterPopoverProps {
	btnClassName?: string;
	children?: ReactNode;
	filterBlocks?: FilterBlockModel[];
	onApplyFilters?: (blocksValues?: BaseBlockFilterModel[]) => void;
	onClearFilters?: () => void;
	onToggleFilters?: (show: boolean) => void;
	isFilterActive?: boolean;
	showFilters?: boolean;
	smallFilterIcon?: boolean;
	popoverPosition?: MenuListPositionOpts;
	contentPosition?: MenuListPositionOpts;
	filterCardWrapperClassName?: string;
	noPaddingFilterCardWrapper?: boolean;
	childrenWrapperClassName?: string;
	loading?: boolean;
}

const FilterPopover: React.FC<FilterPopoverProps> = memo(
	({
		btnClassName,
		children,
		onApplyFilters,
		onClearFilters,
		onToggleFilters,
		smallFilterIcon,
		showFilters = false,
		popoverPosition = MenuListPositionOpts.TOP_RIGHT,
		contentPosition = MenuListPositionOpts.BOTTOM_RIGHT,
		filterCardWrapperClassName = "",
		childrenWrapperClassName = "",
		filterBlocks = [],
		loading
	}) => {
		const { t } = useTranslation();

		const [searchParams, setSearchParams] = useSearchParams();

		const [showFiltersBox, setShowFiltersBox] = useState(showFilters);
		const [filtersData, setFiltersData] = useState<BaseBlockFilterModel[]>([]);

		const filterBtnRef = useRef<HTMLButtonElement>(null);
		type FilterBlocksMethodsHandlers = React.ElementRef<typeof FilterBlocks>;
		const filterBlocksRef = useRef<FilterBlocksMethodsHandlers | null>(null);

		useEffectOnce(() => {
			const newFilters: BaseBlockFilterModel[] = [];
			searchParams.forEach((value, key) => {
				const filterMatch = key.match(/^filter\[(.+)\]$/);
				if (filterMatch?.length === 2) {
					const filterKey = filterMatch[1];
					const originalFilter = filterBlocks.find(x => x.id === filterKey);
					if (originalFilter) {
						newFilters.push({
							blockId: originalFilter.id,
							type: originalFilter.type,
							values: originalFilter.type === FilterBlockTypes.CHECKBOX && value ? value.split(",") : [],
							checked: originalFilter.type === FilterBlockTypes.SWITCHER ? value === "true" : false,
							inputText: originalFilter.type === FilterBlockTypes.INPUT ? value || "" : "",
							radioValue: originalFilter.type === FilterBlockTypes.RADIO ? value || "" : ""
						});
					}
				}
			});

			if (newFilters.length) {
				setFiltersData(newFilters);
				onApplyFilters && onApplyFilters(newFilters);
			}
		});

		const activeFiltersCount = useMemo(
			() => filtersData.filter(x => !!x.values?.length || x.checked || !!x.inputText || !!x.radioValue).length,
			[filtersData]
		);

		const toggleFilters = useCallback(() => {
			setShowFiltersBox(!showFiltersBox);
			onToggleFilters && onToggleFilters(!showFiltersBox);
		}, [showFiltersBox, onToggleFilters]);

		const applyFilters = useCallback(() => {
			toggleFilters();

			const filterBlocksValues = filterBlocksRef.current?.getSelectedOptions
				? filterBlocksRef.current.getSelectedOptions()
				: [];

			setSearchParams(prev => {
				filterBlocksValues.map(block => {
					let val: unknown = [];
					switch (block.type) {
						case FilterBlockTypes.SWITCHER:
							val = (block.checked || false).toString();
							break;

						case FilterBlockTypes.INPUT:
							val = block.inputText || "";
							break;

						case FilterBlockTypes.RADIO:
							val = block.radioValue || "";
							break;

						case FilterBlockTypes.CHECKBOX:
							val = block.values?.length ? block.values.join(",") : [];
							break;
					}
					prev.set(`filter[${block.blockId}]`, val as string);
				});
				return prev;
			});

			setFiltersData(filterBlocksValues);
			onApplyFilters && onApplyFilters(filterBlocksValues);
		}, [toggleFilters, onApplyFilters, setSearchParams]);

		const clearFilters = useCallback(() => {
			toggleFilters();

			onClearFilters && onClearFilters();

			setSearchParams("");

			filterBlocksRef.current?.clearFilters && filterBlocksRef.current.clearFilters();

			setFiltersData([]);
		}, [toggleFilters, onClearFilters, setSearchParams]);

		if (loading) {
			return <SkeletonBase width={42} height={42} fullScale />;
		}

		return (
			<>
				<Button
					ref={filterBtnRef}
					className={clsx(btnClassName)}
					onClick={toggleFilters}
					variant={ButtonVariantsEnum.LINK}
					size={ButtonSizesEnum.MD}
					aria-label="Filter"
					dontFillSvg
					iconButton={{
						icon: (
							<div className="relative">
								<FilterIcon
									className={clsx(
										smallFilterIcon && "w-5 h-5",
										!!activeFiltersCount
											? "svg-paths:stroke-primary-500 svg-paths:fill-primary-500"
											: "svg-paths:stroke-gray-400"
									)}
								/>
								{!!activeFiltersCount && (
									<div className="absolute -top-2.5 -right-2.5 min-w-4 h-4 rounded-full flex items-center justify-center bg-primary-600 p-0.5 px-1">
										<Text variants={TextVariantsEnum.BodySmall} className="text-white">
											{activeFiltersCount}
										</Text>
									</div>
								)}
							</div>
						)
					}}
				/>
				<PopoverBox
					anchorEl={filterBtnRef.current}
					onClose={toggleFilters}
					show={showFiltersBox}
					customMinWidth={287}
					customMaxWidth={360}
					customMaxHeight={"40vh"}
					popoverPosition={popoverPosition}
					contentPosition={contentPosition}
				>
					<FilterCardWrapper
						noPadding
						className={clsx(filterCardWrapperClassName, "max-h-[inherit] h-full overflow-hidden flex flex-col flex-1")}
					>
						<div className="w-full p-4 border-b border-gray-100">
							<Text variants={TextVariantsEnum.Caption3}>{t("filters")}</Text>
						</div>
						<FilterContentWrapper className="flex flex-1 overflow-scroll">
							{!!children && <div className={clsx(childrenWrapperClassName)}>{children}</div>}
							{!!filterBlocks?.length && (
								<div className={clsx(childrenWrapperClassName, "w-full")}>
									<FilterBlocks ref={filterBlocksRef} blocks={filterBlocks} defaultFiltersData={filtersData} />
								</div>
							)}
						</FilterContentWrapper>
						<div className="grid grid-cols-2 gap-2 p-4">
							<Button variant={ButtonVariantsEnum.FILLED} size={ButtonSizesEnum.XS} onClick={applyFilters}>
								{t("apply")} {t("filters").toLowerCase()}
							</Button>
							<Button variant={ButtonVariantsEnum.OUTLINED} size={ButtonSizesEnum.XS} onClick={clearFilters}>
								{t("reset")} {t("filters").toLowerCase()}
							</Button>
						</div>
					</FilterCardWrapper>
				</PopoverBox>
			</>
		);
	}
);

export default FilterPopover;
